import { brand } from "../../config/theme";
// React
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

// Contextos
import { useUserStore } from "../../store/userStore";
import { useActiveAppStore } from "../../store/activeAppStore";
import { useSessionCheck } from "../../hooks/useSessionCheck";
import { setLoggingOut } from "../../utils/tokenUtils";
import { logoutUser } from "../../pages/login/functions";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import { ADMIN_RESELLER_ROLE_ID } from "../../hooks/useIsReseller";
import { isMenuItemEnabled } from "../../config/appsConfig";

// Componentes
import { Header } from "../../components/base/Header";
import { AppsSidebar, useFilteredCategories } from "../../components/base/AppsSidebar";
import { useTheme } from "../../context/ThemeContext";
import type { AppItem } from "../../components/base/AppsSidebar";
import Swal from "sweetalert2";
import { faChartBar } from "@fortawesome/free-solid-svg-icons";

// Utilidades
import { AppSidebarMenu } from "../../components/base/AppSidebarMenu";
import CompanySelector from "../../components/base/CompanySelector";
import { reportMenuEntries } from "../../components/base/AppsRail/reportEntries";

export default function ReportLayout() {
  // Estado globales
  const { t } = useTranslation("common");
  const { themeMode, isDarkMode, setTheme } = useTheme();
  const user = useUserStore((state) => state.user);
  const filteredCategories = useFilteredCategories();
  const hasRedirected = useRef(false);

  // Obtener datos del store de compañías para resellers
  const companyId = useCompaniesStore((state) => state.companyId);
  const allCompanies = useCompaniesStore((state) => state.allCompanies);
  const fetchAllCompanies = useCompaniesStore((state) => state.fetchAllCompanies);

  // Verificar si es reseller por ROL (no por compañía)
  const isReseller = (localStorage.getItem("userRoleId") || "") === ADMIN_RESELLER_ROLE_ID;

  // Set active app for dynamic header title/subtitle and icon
  const setActiveApp = useActiveAppStore((state) => state.setActiveApp);
  useEffect(() => {
    setActiveApp(
      "reportes",
      t("apps.reportes.name"),
      t("apps.reportes.description"),
      faChartBar,
      "bg-orange-500"
    );
  }, [setActiveApp, t]);

  // Sincronizar companyId desde localStorage al montar
  useEffect(() => {
    const storedCompanyId = localStorage.getItem("companyId");
    const storedCompanyUser = localStorage.getItem("userCompanyId");
    if (storedCompanyId && storedCompanyId !== companyId) {
      useCompaniesStore.getState().setCompanyId(storedCompanyId);
    } else if (!storedCompanyId && storedCompanyUser && !isReseller) {
      useCompaniesStore.getState().setCompanyId(storedCompanyUser);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cargar lista de compañías cuando es reseller
  useEffect(() => {
    if (allCompanies.length === 0) {
      fetchAllCompanies();
    }
  }, [isReseller, allCompanies.length, fetchAllCompanies]);

  const {
    isExpiringSoon,
    timeRemainingFormatted,
    isExpired,
    isAuthenticated,
    isInitialized,
  } = useSessionCheck({
    warningThreshold: 60 * 60 * 1000,
    autoRefresh: true,
  });

  // Hooks
  const navigate = useNavigate();
  const location = useLocation();

  // Estado local
  const [isAppsSidebarOpen, setIsAppsSidebarOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const openAppsSidebar = () => setIsAppsSidebarOpen(true);
  const closeAppsSidebar = () => setIsAppsSidebarOpen(false);
  const toggleMobileMenu = () => setIsMobileMenuOpen((prev) => !prev);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const handleAppSelect = (app: AppItem) => {
    if (app.id === "reportes") {
      closeAppsSidebar();
      return;
    }

    if (app.isComingSoon) {
      Swal.fire({
        title: t("appsSidebar.comingSoon"),
        text: t("appsSidebar.comingSoonText", { name: app.name }),
        icon: "info",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }

    if (app.isLocked) {
      Swal.fire({
        title: t("appsSidebar.premiumFeature"),
        text: t("appsSidebar.premiumRequired", { name: app.name }),
        icon: "warning",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }

    if (app.route) {
      closeAppsSidebar();
      navigate(app.route);
    } else if (app.externalUrl) {
      window.open(app.externalUrl, "_blank", "noopener,noreferrer");
    }
  };

  // Agrupados por tema y en el orden en que se piensan (dónde anduvo → cómo
  // manejó → zonas → lo que reporta el equipo). Antes eran doce renglones
  // sueltos que empezaban todos con "Informe de", y había que leerlos todos.
  const menuItems = [
    {
      id: "history",
      label: t("sidebarMenu.historyReport"),
      icon: "ri-history-line",
      group: t("reportsMenu.groups.routes"),
      description: t("reportsMenu.desc.history"),
    },
    {
      id: "routes",
      label: t("sidebarMenu.tripsReport"),
      icon: "ri-route-line",
      group: t("reportsMenu.groups.routes"),
      description: t("reportsMenu.desc.routes"),
    },
    {
      id: "stop",
      label: t("sidebarMenu.stopsReport"),
      icon: "ri-stop-circle-line",
      group: t("reportsMenu.groups.routes"),
      description: t("reportsMenu.desc.stop"),
    },
    {
      id: "mileage",
      label: t("sidebarMenu.mileageReport"),
      icon: "ri-road-map-line",
      group: t("reportsMenu.groups.routes"),
      description: t("reportsMenu.desc.mileage"),
    },
    {
      id: "monthly",
      label: t("sidebarMenu.monthlyReport"),
      icon: "ri-calendar-2-line",
      group: t("reportsMenu.groups.routes"),
      description: t("reportsMenu.desc.monthly"),
    },
    {
      id: "odometer",
      label: t("sidebarMenu.odometerReport"),
      icon: "ri-dashboard-3-line",
      group: t("reportsMenu.groups.routes"),
      description: t("reportsMenu.desc.odometer"),
    },
    {
      id: "behavior",
      label: t("sidebarMenu.behaviorReport"),
      icon: "ri-steering-line",
      group: t("reportsMenu.groups.driving"),
      description: t("reportsMenu.desc.behavior"),
    },
    {
      id: "speed",
      label: t("sidebarMenu.speedReport"),
      icon: "ri-speed-up-line",
      group: t("reportsMenu.groups.driving"),
      description: t("reportsMenu.desc.speed"),
    },
    {
      id: "driving",
      label: t("sidebarMenu.drivingReport"),
      icon: "ri-steering-2-line",
      group: t("reportsMenu.groups.driving"),
      description: t("reportsMenu.desc.driving"),
    },
    {
      // No empieza con "driving": isActiveRoute compara por prefijo y
      // /reports/driving-… encendería también «Conducción».
      id: "workday",
      label: t("sidebarMenu.drivingHistoryReport"),
      icon: "ri-time-line",
      group: t("reportsMenu.groups.driving"),
      description: t("reportsMenu.desc.drivingHistory"),
    },
    {
      id: "geofences",
      label: t("sidebarMenu.geofencesReport"),
      icon: "ri-map-pin-range-line",
      group: t("reportsMenu.groups.zones"),
      description: t("reportsMenu.desc.geofences"),
    },
    {
      id: "events",
      label: t("sidebarMenu.eventsReport"),
      icon: "ri-calendar-event-line",
      group: t("reportsMenu.groups.equipment"),
      description: t("reportsMenu.desc.events"),
    },
    {
      id: "alerts",
      label: t("sidebarMenu.alertsReport"),
      icon: "ri-notification-3-line",
      group: t("reportsMenu.groups.equipment"),
      description: t("reportsMenu.desc.alerts"),
    },
    {
      id: "temperature",
      label: t("sidebarMenu.temperatureReport"),
      icon: "ri-thermometer-line",
      group: t("reportsMenu.groups.equipment"),
      description: t("reportsMenu.desc.temperature"),
    },
    {
      id: "voltage",
      label: t("sidebarMenu.voltageReport"),
      icon: "ri-flashlight-line",
      group: t("reportsMenu.groups.equipment"),
      description: t("reportsMenu.desc.voltage"),
    },
    {
      id: "gps-quality",
      label: t("sidebarMenu.gpsQualityReport"),
      icon: "ri-signal-tower-line",
      group: t("reportsMenu.groups.equipment"),
      description: t("reportsMenu.desc.gpsQuality"),
    },
    {
      id: "commands",
      label: t("sidebarMenu.commandsReport"),
      icon: "ri-terminal-line",
      group: t("reportsMenu.groups.equipment"),
      description: t("reportsMenu.desc.commands"),
    },
    {
      id: "trailer",
      label: t("sidebarMenu.trailerReport"),
      icon: "ri-truck-line",
      group: t("reportsMenu.groups.trailers"),
      description: t("reportsMenu.desc.trailer"),
    },
  ];

  const reportVariants = reportMenuEntries(t);
  const activeVariant = reportVariants.findIndex((e) => e.match.test(location.pathname));
  const hasSideMenu = !/^\/reports\/(quick|scheduled)/.test(location.pathname);

  // Filter menu items based on app config
  const filteredMenuItems = menuItems.filter((item) =>
    isMenuItemEnabled("rastreo-telemetria", "reportes", item.id),
  );

  useEffect(() => {
    if (
      isInitialized &&
      (isExpired || !isAuthenticated) &&
      !hasRedirected.current
    ) {
      hasRedirected.current = true;
      setLoggingOut(false);
      navigate("/");
    }
  }, [isExpired, isAuthenticated, isInitialized, navigate]);

  useEffect(() => {
    if (!user) {
      if (location.pathname !== "/splash") {
        localStorage.setItem("lastRoute", location.pathname);
        navigate("/splash");
      }
    }
  }, [user, location.pathname, navigate]);

  return (
    <div className={`h-screen flex flex-col ${isDarkMode ? "bg-dark-bg" : "bg-gray-50"}`}>
      {/* Banner de sesión por expirar */}
      {isExpiringSoon && (
        <div className="bg-amber-500 text-white px-4 py-2 text-center text-sm flex items-center justify-center gap-2">
          <i className="ri-alarm-warning-line"></i>
          <span>
            {t("sessionBanner.expiring", { time: timeRemainingFormatted })}
          </span>
        </div>
      )}
      <Header
        onOpenAppsSidebar={openAppsSidebar}
        onToggleMobileMenu={toggleMobileMenu}
        isMobileMenuOpen={isMobileMenuOpen}
        isDarkMode={isDarkMode}
        isReseller={isReseller}
        companies={allCompanies}
        selectedCompany={companyId}
        onCompanyChange={(id) => useCompaniesStore.getState().setCompanyId(id)}
      />
      {/* En móvil no existe el riel (es lg:flex), y era la única entrada a
          Informes rápidos y programados: desde el teléfono no había forma de
          llegar, ni de volver a los de análisis una vez ahí. */}
      {activeVariant >= 0 && (
        <nav aria-label={t("appsRail.reports.mobileNav", "Tipo de informe")} className="lg:hidden px-4 pt-3 pb-2">
          <div className={`relative grid grid-cols-3 p-1 rounded-xl ${isDarkMode ? "bg-white/5" : "bg-black/5"}`}>
            <span
              aria-hidden
              className={`absolute inset-y-1 left-1 w-[calc((100%-0.5rem)/3)] rounded-lg shadow-sm transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none ${
                isDarkMode ? "bg-white/15" : "bg-white"
              }`}
              style={{ transform: `translateX(${activeVariant * 100}%)` }}
            />
            {reportVariants.map((entry, i) => (
              <button
                key={entry.id}
                type="button"
                aria-current={i === activeVariant ? "page" : undefined}
                onClick={() => i !== activeVariant && navigate(entry.route)}
                className={`relative z-10 flex items-center justify-center gap-1.5 min-h-10 px-1 rounded-lg text-sm font-medium transition-colors duration-200 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                  i === activeVariant
                    ? isDarkMode ? "text-white" : "text-gray-900"
                    : isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                <i aria-hidden className={`${entry.icon} text-base hidden min-[420px]:inline`} style={i === activeVariant ? { color: brand.primary } : undefined} />
                <span className="truncate">{entry.short ?? entry.label}</span>
              </button>
            ))}
          </div>
          {/* El Header oculta el selector de compañía en móvil y lo deja en el
              menú lateral, que en rápidos y programados no se monta: un
              reseller se quedaba en «Selecciona una compañía» sin salida. */}
          {isReseller && !hasSideMenu && (
            <CompanySelector
              value={companyId ?? null}
              onChange={(id) => useCompaniesStore.getState().setCompanyId(id)}
              isDarkMode={isDarkMode}
              isClearable
              companies={allCompanies.length > 0 ? allCompanies : undefined}
              className="w-full mt-2"
            />
          )}
        </nav>
      )}
      <div className="flex flex-1 min-h-0 overflow-hidden relative">
        {/* Informes rápidos y programados traen su propio catálogo: el menú de
            los doce informes de análisis ahí sólo estorba. */}
        {hasSideMenu && (
          <AppSidebarMenu
            menuItems={filteredMenuItems.map((item) => ({
              ...item,
              path: `/reports/${item.id}`,
            }))}
            isDarkMode={isDarkMode}
            isMobileMenuOpen={isMobileMenuOpen}
            onCloseMobileMenu={closeMobileMenu}
            onNavigate={(path) => navigate(path)}
            isActiveRoute={(itemId) =>
              location.pathname.endsWith(itemId) ||
              location.pathname.startsWith(`/reports/${itemId}`)
            }
            companySelector={
              isReseller ? (
                <CompanySelector
                  value={companyId ?? null}
                  onChange={(id) => useCompaniesStore.getState().setCompanyId(id)}
                  isDarkMode={isDarkMode}
                  isClearable
                  companies={allCompanies.length > 0 ? allCompanies : undefined}
                  className="w-full"
                />
              ) : undefined
            }
            // La compañía ya está en el encabezado (selector del reseller o el
            // nombre bajo el usuario): el recuadro de "Compañía seleccionada"
            // la repetía y empujaba los informes hacia abajo.
            grouped={{
              scope: "reportes",
              searchPlaceholder: t("reportsMenu.search"),
              clearSearchLabel: t("reportsMenu.clearSearch"),
              emptyText: t("reportsMenu.empty"),
              favoritesLabel: t("reportsMenu.favorites"),
              favoritesHint: t("reportsMenu.favoritesHint"),
              addFavoriteLabel: t("reportsMenu.addFavorite"),
              removeFavoriteLabel: t("reportsMenu.removeFavorite"),
            }}
          />
        )}
        {/* Main Content */}
        <div className="flex-1 max-h-full overflow-hidden flex justify-center min-w-0 w-full">
          {isReseller && !companyId ? (
            <div className="flex items-center justify-center h-full w-full">
              <div className="flex flex-col items-center justify-center px-6 py-12">
                <div className={`w-20 h-20 rounded-full ${isDarkMode ? "bg-dark-card" : "bg-gray-100"} flex items-center justify-center mb-6`}>
                  <i className={`ri-building-2-line text-4xl ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}></i>
                </div>
                <h2 className={`text-xl font-semibold ${isDarkMode ? "text-gray-200" : "text-gray-800"} text-center mb-3`}>
                  {t("header.selectCompanyFirst")}
                </h2>
                <p className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-500"} text-center max-w-md`}>
                  {t("header.selectCompanyHint")}
                </p>
              </div>
            </div>
          ) : (
            <Outlet />
          )}
        </div>
      </div>
      <AppsSidebar
        isOpen={isAppsSidebarOpen}
        onClose={closeAppsSidebar}
        categories={filteredCategories}
        selectedAppId="reportes"
        onAppSelect={handleAppSelect}
        onStoreClick={() => navigate("/store")}
        isDarkMode={isDarkMode}
        currentTheme={themeMode}
        onThemeChange={setTheme}
        userInfo={user ? {
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          username: user.username,
        } : undefined}
        onProfileClick={() => {
          closeAppsSidebar();
          navigate("/profile");
        }}
        onLogout={async () => {
          setIsLoggingOut(true);
          await logoutUser();
          setIsLoggingOut(false);
        }}
        isLoggingOut={isLoggingOut}
      />
    </div>
  );
}
